/**
 * Multi-Tab Broadcast Utility
 *
 * Handles coordination of version changes across multiple browser tabs
 * Uses BroadcastChannel API with localStorage fallback for older browsers
 */

/**
 * Channel name for version update broadcasts
 */
const VERSION_CHANNEL_NAME = 'chariot-version-updates';

/**
 * Message types for version broadcasts
 */
type BroadcastMessageType = 'version-changed' | 'storage-cleared';

/**
 * Structure for broadcast messages
 */
interface VersionBroadcastMessage {
  type: BroadcastMessageType;
  payload: {
    version: string;
    timestamp: number;
  };
}

/**
 * Version Broadcast Service
 *
 * Manages communication between tabs about version changes
 * Ensures all tabs update together for consistent user experience
 */
class VersionBroadcastService {
  private channel: BroadcastChannel | null = null;
  private listeners: Array<(message: VersionBroadcastMessage) => void> = [];
  private isInitialized = false;

  /**
   * Initialize the broadcast service
   *
   * Uses BroadcastChannel if available, falls back to localStorage events
   */
  initialize(): void {
    if (this.isInitialized) {
      return;
    }

    try {
      // Check if BroadcastChannel is available
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        this.channel = new BroadcastChannel(VERSION_CHANNEL_NAME);
        this.setupBroadcastListener();
      } else {
        // Fallback to storage events for older browsers
        this.setupStorageListener();
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('[Broadcast] Failed to initialize:', error);
    }
  }

  /**
   * Setup BroadcastChannel listener (modern browsers)
   */
  private setupBroadcastListener(): void {
    if (!this.channel) return;

    this.channel.addEventListener('message', (event: MessageEvent) => {
      try {
        const message = event.data as VersionBroadcastMessage;
        this.handleMessage(message);
      } catch (error) {
        console.error('[Broadcast] Failed to handle message:', error);
      }
    });
  }

  /**
   * Setup localStorage listener (fallback for older browsers)
   */
  private setupStorageListener(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('storage', (event: StorageEvent) => {
      // Only handle version broadcast messages
      if (event.key === VERSION_CHANNEL_NAME && event.newValue) {
        try {
          const message = JSON.parse(event.newValue) as VersionBroadcastMessage;
          this.handleMessage(message);
        } catch (error) {
          console.error('[Broadcast] Failed to parse storage event:', error);
        }
      }
    });
  }

  /**
   * Handle incoming broadcast messages
   */
  private handleMessage(message: VersionBroadcastMessage): void {
    if (message.type === 'version-changed') {
      // Notify all registered listeners
      this.listeners.forEach(listener => {
        try {
          listener(message);
        } catch (error) {
          console.error('[Broadcast] Listener error:', error);
        }
      });
    }
  }

  /**
   * Broadcast version change to other tabs
   *
   * @param version - New version string
   */
  notifyVersionChange(version: string): void {
    if (!this.isInitialized) {
      this.initialize();
    }

    const message: VersionBroadcastMessage = {
      type: 'version-changed',
      payload: {
        version,
        timestamp: Date.now(),
      },
    };

    try {
      if (this.channel) {
        // Use BroadcastChannel
        this.channel.postMessage(message);
      } else {
        // Fallback to localStorage for cross-tab communication
        localStorage.setItem(VERSION_CHANNEL_NAME, JSON.stringify(message));
        // Clean up immediately to avoid cluttering storage
        localStorage.removeItem(VERSION_CHANNEL_NAME);
      }
    } catch (error) {
      console.error('[Broadcast] Failed to notify version change:', error);
    }
  }

  /**
   * Register a listener for version change broadcasts
   *
   * @param callback - Function to call when version change is broadcast
   * @returns Cleanup function to remove listener
   */
  onVersionChange(
    callback: (message: VersionBroadcastMessage) => void
  ): () => void {
    if (!this.isInitialized) {
      this.initialize();
    }

    this.listeners.push(callback);

    // Return cleanup function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Close the broadcast channel and cleanup
   */
  close(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners = [];
    this.isInitialized = false;
  }
}

/**
 * Singleton instance of the broadcast service
 */
export const versionBroadcast = new VersionBroadcastService();

/**
 * Notify other tabs about version change
 *
 * @param version - New version string to broadcast
 */
export function notifyVersionChange(version: string): void {
  versionBroadcast.notifyVersionChange(version);
}

/**
 * Listen for version changes from other tabs
 *
 * @param callback - Function to call when version change is detected
 * @returns Cleanup function to remove listener
 */
export function listenForVersionChanges(
  callback: (oldVersion: string, newVersion: string) => void
): () => void {
  return versionBroadcast.onVersionChange(message => {
    // For simplicity, pass the new version twice
    // The hook will determine the old version from storage
    callback(message.payload.version, message.payload.version);
  });
}
